import { Pto } from '@outcoreaix/pto'
import { AppRoutes } from 'app/app-routes'
import { Link } from 'react-router-dom'

import Icon, {
  AppstoreOutlined,
  AreaChartOutlined,
  CommentOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  InteractionOutlined,
  ProductOutlined,
  ProjectOutlined,
  RocketOutlined,
  ReadOutlined,
  SettingOutlined,
  StarOutlined,
  StopOutlined,
  ToolOutlined,
  HomeOutlined,
  BellOutlined,
  EyeOutlined,
  MailOutlined
} from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { setActiveChatId, setCurrentSpaceId } from 'features/chats/chatSlice'
import { useCpdLogin } from 'features/learn-worlds'
import { Badge, Spin } from 'antd'
import { ItemType, MenuItemType } from 'antd/es/menu/interface'
import SpacesIcon from 'assets/spaces-icon'
import NewChatIcon from 'assets/new-chat-icon'

export const generateSystemAdminMenuItems = (onClick?: () => void) => {
  return [
    {
      key: AppRoutes.workspaces,
      icon: <AppstoreOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.workspaces}>
          Workspaces
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.feedbacks,
      icon: <CommentOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.feedbacks}>
          Feedbacks
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.appSettings,
      icon: <SettingOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.appSettings}>
          App Settings
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.conversationStartersManagement,
      icon: <RocketOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.conversationStartersManagement}>
          Conversation Starter Management
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.systemAdminDashboard,
      icon: <AreaChartOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.systemAdminDashboard}>
          Dashboard
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.changelogManagement,
      icon: <BellOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.changelogManagement}>
          What&apos;s New
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.changelogUserReadsAudit,
      icon: <EyeOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.changelogUserReadsAudit}>
          What&apos;s New — read log
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.dbDocsViewer,
      icon: <DatabaseOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.dbDocsViewer}>
          DB docs
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.toolManagement,
      icon: <ToolOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.toolManagement}>
          AI Tools Management
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.productManagement,
      icon: <ProductOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.productManagement}>
          Product management
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.flows,
      icon: <InteractionOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.flows}>
          Flows
        </Link>
      ),
      className: '!pl-2'
    },
    {
      key: AppRoutes.emailTemplates,
      icon: <MailOutlined />,
      label: (
        <Link onClick={onClick} to={AppRoutes.emailTemplates}>
          Email Templates
        </Link>
      ),
      className: '!pl-2'
    }
  ]
}

export const generateUserMenuItems = (
  workspace?: Pto.Workspaces.WorkspaceOption,
  onClick?: () => void,
  spaces?: Pto.Spaces.SpaceListItem[],
  onCreateChat?: () => Promise<void | Pto.Chats.Chat>,
  isAIConfigSet?: boolean,
  scopeId?: number,
  currentWorkspaceId?: number,
  isDesktop?: boolean,
  isCollapsed?: boolean,
  disableNewChat?: boolean
): ItemType<MenuItemType>[] => {
  const dispatch = useDispatch()
  const { login, isFetching, cpdStatus } = useCpdLogin()

  const isWorkspaceAdminOrStaff = [Pto.Workspaces.WorkspaceRole.Admin, Pto.Workspaces.WorkspaceRole.Staff].includes(
    workspace?.currentMember?.role as Pto.Workspaces.WorkspaceRole
  )
  // Mirrors the condition on the New chat item below; the single-space item is only dropped when
  // New chat is actually there to replace it.
  const hasNewChatItem = !!(isAIConfigSet && onCreateChat && scopeId && currentWorkspaceId)

  return [
    ...(workspace?.currentMember?.role !== Pto.Workspaces.WorkspaceRole.Contact
      ? [
          {
            key: `${AppRoutes.workspaces}/${workspace?.id}/dashboard`,
            icon: (
              <div className="rotate-180 inline-block">
                <ProjectOutlined />
              </div>
            ),
            label: (
              <Link onClick={onClick} to={`${AppRoutes.workspaces}/${workspace?.id}/dashboard`}>
                Dashboard
              </Link>
            ),
            className: '!pl-2'
          },
          workspace?.currentMember?.tags.includes(Pto.Workspaces.WorkspaceMemberTag.Safeguarding)
            ? {
                key: `${AppRoutes.workspaces}/${workspace?.id}${AppRoutes.alerts}`,
                icon: <ExclamationCircleOutlined />,
                label: (
                  <Link onClick={onClick} to={`${AppRoutes.workspaces}/${workspace?.id}${AppRoutes.alerts}`}>
                    Alerts
                  </Link>
                ),
                className: '!pl-2'
              }
            : null
        ]
      : []),
    ...(isWorkspaceAdminOrStaff
      ? [
          {
            key: `${AppRoutes.workspaces}/${workspace?.id}${AppRoutes.interviews}`,
            icon: <StarOutlined />,
            label: (
              <Link onClick={onClick} to={`${AppRoutes.workspaces}/${workspace?.id}${AppRoutes.interviews}`}>
                Interviews
              </Link>
            ),
            className: '!pl-2'
          },
          {
            key: 'cpd-action',
            icon: (
              <Badge
                count={cpdStatus?.completed === false ? 1 : 0}
                size="small"
                offset={isCollapsed ? [0, 7] : [0, -4]}
                className="cpd-badge-transition"
                onClick={login}
              >
                <ReadOutlined />
              </Badge>
            ),
            className: '!pl-2',
            label: (
              <div onClick={login} className="flex gap-2 items-center max-h-[40px] w-full">
                CPD Module
                {!isDesktop && isFetching && <Spin size="small" />}
              </div>
            )
          }
        ]
      : []),
    // An admin/staff member of a single space has no use for this item: it opens the same space
    // page in a fresh-chat state as the New chat item directly below it (OT-1280). Contact users
    // keep it, and so does anyone without a New chat item, for whom it is the only way into the
    // space from the sidebar.
    ...(spaces?.length === 1 && !(isWorkspaceAdminOrStaff && hasNewChatItem)
      ? [
          {
            key: `${AppRoutes.workspaces}/${workspace?.id}/space/${spaces[0].id}`,
            icon: <Icon component={() => <SpacesIcon />} />,
            label: (
              <Link
                onClick={() => {
                  dispatch(setActiveChatId(undefined))
                  dispatch(setCurrentSpaceId(spaces[0].id))
                  onClick?.()
                }}
                to={`${AppRoutes.workspaces}/${workspace?.id}/space/${spaces[0].id}`}
                className={
                  spaces[0].status === Pto.Spaces.SpaceStatus.Inactive ? 'truncate pointer-events-none' : 'truncate'
                }
              >
                {spaces[0].status === Pto.Spaces.SpaceStatus.Inactive && <StopOutlined className="text-red-500 mr-1" />}
                {spaces[0].name} Space
              </Link>
            ),
            className: '!pl-2'
          }
        ]
      : []),
    ...(spaces?.length === 1
      ? []
      : [
          {
            key: `spaces_chat`,
            icon: <Icon component={() => <SpacesIcon />} />,
            label: 'Spaces',
            className: `xl:!pl-0 [&_.ant-menu-submenu-title]:!pl-2 ${isCollapsed ? '' : 'max-h-[30vh] overflow-auto'}`,
            disabled: !spaces?.length,
            children: spaces?.map((space) => ({
              key: `${AppRoutes.workspaces}/${workspace?.id}/space/${space.id}`,
              disabled: space.status === Pto.Spaces.SpaceStatus.Inactive,
              className: '!pl-9',
              label: (
                <Link
                  onClick={() => {
                    dispatch(setActiveChatId(undefined))
                    dispatch(setCurrentSpaceId(space.id))
                    onClick?.()
                  }}
                  to={`${AppRoutes.workspaces}/${workspace?.id}/space/${space.id}`}
                  className={`${space.status === Pto.Spaces.SpaceStatus.Inactive ? 'pointer-events-none click:bg-transparent' : ''}`}
                >
                  {space.status === Pto.Spaces.SpaceStatus.Inactive && <StopOutlined className="text-red-500 mr-1" />}
                  {space.name}
                </Link>
              )
            }))
          }
        ]),
    ...(workspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Contact
      ? [
          {
            key: `home_page`,
            icon: <HomeOutlined />,
            label: (
              <Link
                onClick={() => {
                  dispatch(setActiveChatId(undefined))
                  onClick?.()
                }}
                to={`${AppRoutes.workspaces}/${currentWorkspaceId}/space/${scopeId}/home`}
              >
                Home
              </Link>
            ),
            className: '!pl-2'
          }
        ]
      : []),
    ...(hasNewChatItem
      ? [
          {
            key: `new-chat-${scopeId}`,
            icon: <Icon component={() => <NewChatIcon />} />,
            disabled: !!disableNewChat,
            label: (
              <Link
                onClick={async (e) => {
                  e.preventDefault()
                  if (disableNewChat) return
                  await onCreateChat?.()
                  onClick?.()
                }}
                to={`${AppRoutes.workspaces}/${currentWorkspaceId}/space/${scopeId}`}
              >
                New chat
              </Link>
            ),
            className: '!pl-2'
          }
        ]
      : [])
  ]
}

export const generateSpaceSettingsMenu = (
  workspaceId?: number,
  spaceId?: number,
  role?: Pto.Spaces.SpaceMemberRole,
  onClick?: () => void
) => {
  if (
    role !== Pto.Spaces.SpaceMemberRole.Admin &&
    role !== Pto.Spaces.SpaceMemberRole.Staff &&
    role !== Pto.Spaces.SpaceMemberRole.Learner
  ) {
    return []
  }

  if (role === Pto.Spaces.SpaceMemberRole.Admin) {
    return [
      {
        key: `/settings/${workspaceId}/space/${spaceId}/general`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/general`}>
            General
          </Link>
        )
      },
      {
        key: `/settings/${workspaceId}/space/${spaceId}/ai-config`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/ai-config`}>
            AI Configuration
          </Link>
        )
      },
      // {
      //   key: `/settings/${workspaceId}/space/${spaceId}/onboarding-interview`,
      //   label: (
      //     <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/onboarding-interview`}>
      //       Induction Questions
      //     </Link>
      //   )
      // }
      {
        key: `/settings/${workspaceId}/space/${spaceId}/members`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/members`}>
            Members
          </Link>
        )
      },
      {
        key: `/settings/${workspaceId}/space/${spaceId}/custom-emails-history`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/custom-emails-history`}>
            Emails History
          </Link>
        )
      }
    ]
  }

  if (role === Pto.Spaces.SpaceMemberRole.Staff) {
    return [
      {
        key: `/settings/${workspaceId}/space/${spaceId}/members`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/members`}>
            Members
          </Link>
        )
      },
      {
        key: `/settings/${workspaceId}/space/${spaceId}/custom-emails-history`,
        label: (
          <Link onClick={onClick} to={`/settings/${workspaceId}/space/${spaceId}/custom-emails-history`}>
            Emails History
          </Link>
        )
      }
    ]
  }

  return []
}

export const generateWorkspaceSettingsMenu = (workspace?: Pto.Workspaces.WorkspaceOption, onClick?: () => void) => [
  ...(workspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Admin
    ? [
        {
          key: `/settings/${workspace?.id}/general`,
          label: (
            <Link onClick={onClick} to={`/settings/${workspace?.id}/general`}>
              General
            </Link>
          )
        }
      ]
    : []),
  {
    key: `/settings/${workspace?.id}/members`,
    label: (
      <Link onClick={onClick} to={`/settings/${workspace?.id}/members`}>
        Members
      </Link>
    )
  }
]
